import { useSyncExternalStore } from 'react';

export const DEPTH_BANDS = [
  '0–5 m',
  '5–10 m',
  '10–20 m',
  '20–30 m',
  '30–40 m',
  '40 m+',
] as const;

export type DepthBand = typeof DEPTH_BANDS[number];

export interface DepthBandValue {
  band: DepthBand;
  value: number; // 0–100, 0 = not reported
}

export interface CommunityReport {
  id: string;
  siteId: string;
  diverName: string;
  visibility: number; // 0–100 overall
  depthBands?: DepthBandValue[];
  maxDepthMeters?: number | null;
  comment?: string;
  timestamp: string; // ISO
  surfaceCondition?: string | null;
}

/** Upper limit of each depth band in meters. 40m+ returns Infinity. */
export const DEPTH_BAND_MAX: Record<DepthBand, number> = {
  '0–5 m': 5,
  '5–10 m': 10,
  '10–20 m': 20,
  '20–30 m': 30,
  '30–40 m': 40,
  '40 m+': Infinity,
};

/** Lower limit of each depth band. */
export const DEPTH_BAND_MIN: Record<DepthBand, number> = {
  '0–5 m': 0,
  '5–10 m': 5,
  '10–20 m': 10,
  '20–30 m': 20,
  '30–40 m': 30,
  '40 m+': 40,
};

/** Returns true if this band is within the diver's max depth. */
export const isBandDived = (band: DepthBand, maxDepth: number | null | undefined): boolean => {
  if (maxDepth == null) return true; // no limit set → all bands available
  return DEPTH_BAND_MIN[band] < maxDepth;
};

// No seed data — clean start
const seedReports: CommunityReport[] = [];

let reports: CommunityReport[] = seedReports;
let listeners = new Set<() => void>();

const emitChange = () => listeners.forEach((l) => l());

export const addCommunityReport = (report: Omit<CommunityReport, 'id' | 'timestamp'>) => {
  const newReport: CommunityReport = {
    ...report,
    id: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
  };
  reports = [newReport, ...reports];
  emitChange();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => reports;

export const useCommunityReports = () => useSyncExternalStore(subscribe, getSnapshot);

export const useSiteReports = (siteId: string) => {
  const all = useCommunityReports();
  return all
    .filter((r) => r.siteId === siteId)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
};

export const getVisibilityLabel = (v: number): string => {
  if (v >= 90) return 'Glasklar';
  if (v >= 75) return 'Sehr gut';
  if (v >= 60) return 'Gut';
  if (v >= 45) return 'OK';
  if (v >= 30) return 'Leicht trüb';
  if (v >= 15) return 'Trüb';
  return 'Sehr trüb';
};

export const getVisibilityColor = (v: number): string => {
  if (v >= 60) return 'text-primary';
  if (v >= 45) return 'text-muted-foreground';
  if (v >= 30) return 'text-accent';
  return 'text-rating';
};

/** Primary bands (0–30m) are weighted 2×, deeper 1×. Only reported bands count. */
const PRIMARY_BANDS = new Set<DepthBand>(['0–5 m', '5–10 m', '10–20 m', '20–30 m']);

export const computeOverallFromBands = (bands: DepthBandValue[]): number => {
  const reported = bands.filter((b) => b.value > 0);
  if (reported.length === 0) return 0;
  let weightedSum = 0;
  let totalWeight = 0;
  for (const b of reported) {
    const w = PRIMARY_BANDS.has(b.band) ? 2 : 1;
    weightedSum += b.value * w;
    totalWeight += w;
  }
  return Math.round(weightedSum / totalWeight);
};

/** Get displayable depth entries from a report */
export const getReportDepthEntries = (r: CommunityReport): Array<{ label: string; value: number }> => {
  if (r.depthBands && r.depthBands.length > 0) {
    return r.depthBands.filter((b) => b.value > 0).map((b) => ({ label: b.band, value: b.value }));
  }
  return [];
};
